
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pencil, Tag } from "lucide-react";
import { Slab } from "@/types/slab";
import { useState } from "react";
import { SlabEditForm } from "./SlabEditForm";
import { SlabImageViewer } from "./slab/SlabImageViewer";
import { SlabInfoGrid } from "./slab/SlabInfoGrid";
import { SlabLabel } from "./slab/SlabLabel";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SlabDetailsProps {
  slab: Slab;
  open: boolean;
  onClose: () => void;
  onUpdate?: (updatedSlab: Slab) => void;
}

export function SlabDetails({ slab: initialSlab, open, onClose, onUpdate }: SlabDetailsProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [currentSlab, setCurrentSlab] = useState(initialSlab);
  const [showLabel, setShowLabel] = useState(false);
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

  const handleUpdate = async (updatedSlab: Slab) => {
    try {
      const { error } = await supabase
        .from('cat_slabs')
        .update({
          code: updatedSlab.code,
          name: updatedSlab.name,
          type: updatedSlab.type,
          location_id: updatedSlab.location_id,
          price: updatedSlab.price,
          price_per_sqft: updatedSlab.pricePerSqft,
          sqft: updatedSlab.sqft,
          quantity: updatedSlab.quantity,
          image_url: updatedSlab.imageUrl,
          last_updated: new Date().toISOString(),
          dimensions: updatedSlab.dimensions,
        })
        .eq('id', updatedSlab.id);

      if (error) throw error;

      toast.success("Produto atualizado com sucesso!");
      setCurrentSlab(updatedSlab);
      onUpdate?.(updatedSlab);
      setIsEditing(false);
    } catch (error) {
      console.error('Erro ao atualizar produto:', error);
      toast.error("Falha ao atualizar produto");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {currentSlab.name}
                <Badge>{currentSlab.code}</Badge>
                <Badge 
                  variant="secondary" 
                  className="bg-[#E5DEFF] hover:bg-[#8B5CF6] hover:text-white cursor-pointer transition-colors"
                  onClick={() => setShowLabel(true)}
                >
                  <Tag className="w-3 h-3 mr-1" />
                  Label
                </Badge>
              </div>
              {isAuthenticated && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setIsEditing(true)}
                  className="ml-auto mr-8"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {isEditing ? (
            <SlabEditForm
              slab={currentSlab}
              onClose={() => setIsEditing(false)}
              onUpdate={handleUpdate}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 overflow-y-auto max-h-[calc(90vh-8rem)]">
              <SlabImageViewer slab={currentSlab} />
              <SlabInfoGrid slab={currentSlab} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showLabel} onOpenChange={setShowLabel}>
        <DialogContent className="max-w-[90vw] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Etiqueta - {currentSlab.code}</DialogTitle>
          </DialogHeader>
          <div className="w-full h-[calc(90vh-8rem)]">
            <SlabLabel slab={currentSlab} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
